import React, { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  PERMISSIONS,
} from "../config/permissions";

// Create Permission Context
const PermissionContext = createContext(null);

// Permission Provider Component
export const PermissionProvider = ({ children }) => {
  const { user, userPermissions, isMainVendor } = useSelector((state) => state.user);

  // Memoize user data to prevent unnecessary re-renders
  const memoizedUserData = useMemo(
    () => ({
      permissions: userPermissions || {},
      isMainVendor: isMainVendor || false,
      isAuthenticated: !!user,
      userId: user?.id,
      role: user?.role,
    }),
    [userPermissions, isMainVendor, user?.id, user?.role]
  );

  // Memoized permission checking functions
  const permissionUtils = useMemo(() => {
    const { permissions, isMainVendor, isAuthenticated } = memoizedUserData;

    return {
      // Basic permission check
      canPerform: (module, action) => {
        if (!isAuthenticated) return false;
        return hasPermission(permissions, isMainVendor, module, action);
      },

      // Check if user has ANY of the provided actions for a module
      canPerformAny: (module, actions = []) => {
        if (!isAuthenticated) return false;
        return hasAnyPermission(permissions, isMainVendor, module, actions);
      },

      // Check if user has ALL of the provided actions for a module
      canPerformAll: (module, actions = []) => {
        if (!isAuthenticated) return false;
        return hasAllPermissions(permissions, isMainVendor, module, actions);
      },

      // Quick role-based checks
      isMainVendor,
      isAuthenticated,

      // Module-specific permission checkers (memoized)
      roles: {
        canView: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.ROLES_PERMISSION,
            PERMISSIONS.ACTIONS.ROLE_VIEW
          ),
        canCreate: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.ROLES_PERMISSION,
            PERMISSIONS.ACTIONS.ROLE_CREATE
          ),
        canEdit: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.ROLES_PERMISSION,
            PERMISSIONS.ACTIONS.ROLE_EDIT
          ),
        canDelete: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.ROLES_PERMISSION,
            PERMISSIONS.ACTIONS.ROLE_DELETE
          ),
        canManage: () =>
          hasAnyPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.ROLES_PERMISSION,
            [
              PERMISSIONS.ACTIONS.ROLE_VIEW,
              PERMISSIONS.ACTIONS.ROLE_CREATE,
              PERMISSIONS.ACTIONS.ROLE_EDIT,
            ]
          ),
        hasFullAccess: () =>
          hasAllPermissions(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.ROLES_PERMISSION,
            [
              PERMISSIONS.ACTIONS.ROLE_VIEW,
              PERMISSIONS.ACTIONS.ROLE_CREATE,
              PERMISSIONS.ACTIONS.ROLE_EDIT,
              PERMISSIONS.ACTIONS.ROLE_DELETE,
            ]
          ),
      },

      deals: {
        canView: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.DEALS_MANAGEMENT,
            PERMISSIONS.ACTIONS.VIEW_DEALS
          ),
        canCreate: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.DEALS_MANAGEMENT,
            PERMISSIONS.ACTIONS.CREATE_OFFER
          ),
        canEdit: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.DEALS_MANAGEMENT,
            PERMISSIONS.ACTIONS.EDIT_OFFER
          ),
        canDelete: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.DEALS_MANAGEMENT,
            PERMISSIONS.ACTIONS.DELETE_OFFER
          ),
        canBlast: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.DEALS_MANAGEMENT,
            PERMISSIONS.ACTIONS.BLAST_DEAL
          ),
        canManage: () =>
          hasAnyPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.DEALS_MANAGEMENT,
            [
              PERMISSIONS.ACTIONS.VIEW_DEALS,
              PERMISSIONS.ACTIONS.CREATE_OFFER,
              PERMISSIONS.ACTIONS.EDIT_OFFER,
            ]
          ),
      },

      partners: {
        canView: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
            PERMISSIONS.ACTIONS.VIEW_PARTNERS
          ),
        canCreate: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
            PERMISSIONS.ACTIONS.CREATE_PARTNER
          ),
        canEdit: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
            PERMISSIONS.ACTIONS.EDIT_PARTNER
          ),
        canDelete: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
            PERMISSIONS.ACTIONS.DELETE_PARTNER
          ),
        canManage: () =>
          hasAnyPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
            [
              PERMISSIONS.ACTIONS.VIEW_PARTNERS,
              PERMISSIONS.ACTIONS.CREATE_PARTNER,
              PERMISSIONS.ACTIONS.EDIT_PARTNER,
            ]
          ),
      },

      subscription: {
        canView: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.SUBSCRIPTION,
            PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION
          ),
        canPurchase: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.SUBSCRIPTION,
            PERMISSIONS.ACTIONS.PURCHASE_SUBSCRIPTION
          ),
        canViewHistory: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.SUBSCRIPTION,
            PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION_HISTORY
          ),
      },

      analytics: {
        canView: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.DEALS_ANALYTICS,
            PERMISSIONS.ACTIONS.VIEW_REPORTS_ANALYTICS
          ),
      },

      redeem: {
        canRedeem: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.REDEEM_OFFERS,
            PERMISSIONS.ACTIONS.REDEEM_OFFERS
          ),
        canEdit: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.REDEEM_OFFERS,
            PERMISSIONS.ACTIONS.REDEEM_EDIT
          ),
        canDelete: () =>
          hasPermission(
            permissions,
            isMainVendor,
            PERMISSIONS.MODULES.REDEEM_OFFERS,
            PERMISSIONS.ACTIONS.REDEEM_DELETE
          ),
      },
    };
  }, [memoizedUserData]);

  // Memoized context value
  const contextValue = useMemo(
    () => ({
      ...permissionUtils,
      user: memoizedUserData,
    }),
    [permissionUtils, memoizedUserData]
  );

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

// Custom hook to use permission context
export const usePermissions = () => {
  const context = useContext(PermissionContext);

  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }

  return context;
};

// Higher-order component for permission-based rendering
export const withPermissions = (WrappedComponent) => {
  return React.memo((props) => {
    const permissions = usePermissions();
    return <WrappedComponent {...props} permissions={permissions} />;
  });
};

export default PermissionProvider;
